import React, { useEffect, useState } from "react"
import Markdown from "markdown-to-jsx"
import { Loader2 } from "lucide-react"
import { convertOrderFileToMarkdown } from "@/lib/orderFileSupport"
import { UploadedFile } from "@/hooks/useExtractDataFromOrder"
import { useTranslation } from "react-i18next"

type OrderMarkdownPreviewProps = {
  file: UploadedFile
}

// Preview pane for office documents in the verify step. PDFs and images use
// the bundled pdf.js/image viewers; anything anydoc-convertible is rendered
// as Markdown so the dispatcher sees the same text the extraction saw.
const OrderMarkdownPreview: React.FC<OrderMarkdownPreviewProps> = ({
  file,
}) => {
  const { t } = useTranslation()
  const [markdown, setMarkdown] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setMarkdown(null)
    setError(false)

    const load = async () => {
      try {
        const data = file.file
          ? await file.file.arrayBuffer()
          : await (await fetch(file.url)).arrayBuffer()
        const result = await convertOrderFileToMarkdown(data, file.name)
        if (!cancelled) setMarkdown(result)
      } catch (e) {
        console.error("Failed to render document preview:", e)
        if (!cancelled) setError(true)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [file])

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        {t("orders.fileSupport.previewFailed")}
      </div>
    )
  }

  if (markdown === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="prose prose-sm mx-auto max-w-4xl px-8 py-6 prose-table:text-xs">
        <Markdown>{markdown}</Markdown>
      </div>
    </div>
  )
}

export default OrderMarkdownPreview
